import { CurrencyType } from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types/auto-generator-for-view-model";

type Nullable<T> = T | null | undefined;

/**
 * SC 連携トグルが読み書きする ViewModel
 *
 * スキーマ上必須のキーは必須。Zod `.nullish()` 由来で ViewModel 上もキーが
 * optional になるものだけ `?` を残す（nullish は value 側の Nullable に閉じる）。
 */
export type ScConnectedToggleViewModel = {
	isSCConnected?: ForNodeType<Nullable<boolean>>;
	isRoomPriceFree: ForNodeType<boolean>;
	staticPriceParams?: object | null | undefined;
	price: {
		amount: ForNodeType<number>;
		currencyType: ForNodeType<CurrencyType>;
	};
};

/**
 * onSetViewModel(<K>(values: { key: K; value: V[K] }[])) が異種キー配列を受け取るための戻り値型。
 * K が keyof V に推論されると呼び出し側のキャストが不要になる。
 */
type ScConnectedViewModelUpdates<TViewModel extends ScConnectedToggleViewModel> = {
	key: keyof TViewModel;
	value: TViewModel[keyof TViewModel];
}[];

/**
 * SC 連携トグル OFF→ON 時の ViewModel 更新を作成する。
 *
 * ユーザー操作で変えるのは isSCConnected のみ。他フィールドは SC ON 時の正規化値であり、
 * 保存時の fixDataInconsistencies（サーバー）でも同様に寄せられる。
 * ここで即時正規化するのは、保存前に disabled な UI に古い値が残らないため。
 *
 * 戻り値の型アサーションは、リテラル配列をジェネリック ViewModel の更新型へ
 * 対応づける境界。呼び出し側では不要。
 */
export const createScConnectedOnViewModelUpdates = <TViewModel extends ScConnectedToggleViewModel>(
	viewModel: TViewModel,
): ScConnectedViewModelUpdates<TViewModel> => {
	const updates = [
		// ユーザー意図: SC 連携を ON にする
		{
			key: "isSCConnected" as const,
			value: {
				...viewModel.isSCConnected,
				isEdited: true as const,
				value: true,
			},
		},
		// 以下は SC ON 時の正規化（UI 即時反映。保存時はサーバー側でも同値に寄せる）
		{
			key: "staticPriceParams" as const,
			value: undefined,
		},
		{
			key: "isRoomPriceFree" as const,
			value: {
				...viewModel.isRoomPriceFree,
				isEdited: true as const,
				value: false,
			},
		},
		{
			key: "price" as const,
			value: {
				...viewModel.price,
				amount: {
					...viewModel.price.amount,
					isEdited: true as const,
					value: 0,
				},
				currencyType: {
					...viewModel.price.currencyType,
					isEdited: true as const,
					value: viewModel.price.currencyType.value ?? CurrencyType.Jpy,
				},
			},
		},
	];

	return updates as ScConnectedViewModelUpdates<TViewModel>;
};
